/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PriorityRoutingPage.tsx
 🗓️ Created: 2025-12-10
 🧩 Description:
    Priority Routing list page - manage call priority rules
 ────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ArrowUp,
    Plus,
    Edit,
    Trash2,
    Check,
    X,
    Loader2,
    Copy,
} from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import {
    fetchPriorityRouting,
    deletePriorityRouting,
    togglePriorityRouting,
    PriorityRoutingEntry,
    PriorityRoutingListParams,
} from './api';
import { useContextStore } from '../../store/context';

interface ColumnDef {
    key: string;
    label: string;
    visible: boolean;
    sortable?: boolean;
    render?: (value: unknown, item: PriorityRoutingEntry) => React.ReactNode;
}

export default function PriorityRoutingPage() {
    const navigate = useNavigate();
    const { t } = useTranslation('priorityRouting');
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();

    // State
    const [data, setData] = useState<PriorityRoutingEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('priority');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Delete modal state
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; entry: PriorityRoutingEntry | null }>({
        isOpen: false,
        entry: null,
    });

    // Toggle loading states
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

    // Column visibility
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
        name: true,
        priority: true,
        enabled: true,
        actions: true,
    });

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params: PriorityRoutingListParams = {
                page,
                limit,
                search: search || undefined,
                sortBy,
                sortOrder,
                domainId: selectedDomain?.id,
            };
            const result = await fetchPriorityRouting(params);
            setData(result.data || []);
            if (result.pagination) {
                setTotal(result.pagination.total);
                setTotalPages(result.pagination.totalPages);
            }
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to load priority routing');
        } finally {
            setLoading(false);
        }
    }, [page, limit, search, sortBy, sortOrder, showError, selectedDomain?.id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handlers - Navigate to form instead of opening modal
    const handleCreate = useCallback(() => {
        navigate('/pbx/call-center/priority-routing/new');
    }, [navigate]);

    const handleEdit = useCallback((entry: PriorityRoutingEntry) => {
        navigate(`/pbx/call-center/priority-routing/${(entry as any).uuid || entry.id}`);
    }, [navigate]);

    const handleDelete = useCallback((entry: PriorityRoutingEntry) => {
        setDeleteModal({ isOpen: true, entry });
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteModal.entry) return;

        try {
            await deletePriorityRouting(deleteModal.entry.id);
            showSuccess(t('deleteSuccess', 'Priority routing deleted successfully'));
            setDeleteModal({ isOpen: false, entry: null });
            loadData();
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to delete');
        }
    }, [deleteModal.entry, loadData, showSuccess, showError, t]);

    const handleToggle = useCallback(async (entry: PriorityRoutingEntry) => {
        setTogglingIds(prev => new Set([...prev, entry.id]));
        try {
            await togglePriorityRouting(entry.id);
            setData(prev => prev.map(e =>
                e.id === entry.id ? { ...e, enabled: !e.enabled } : e
            ));
            showSuccess(t('toggleSuccess', 'Status updated'));
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to toggle status');
        } finally {
            setTogglingIds(prev => {
                const next = new Set(prev);
                next.delete(entry.id);
                return next;
            });
        }
    }, [showSuccess, showError, t]);

    const handleSort = useCallback((column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    }, [sortBy, sortOrder]);

    const handleColumnToggle = useCallback((key: string) => {
        setColumnVisibility(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    // Priority badge color
    const getPriorityColor = (priority: number) => {
        if (priority >= 8) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
        if (priority >= 5) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    };

    // Columns
    const columns: ColumnDef[] = [
        {
            key: 'name',
            label: t('columns.name', 'Name'),
            visible: columnVisibility.name,
            sortable: true,
            render: (_, item) => (
                <div className="flex flex-col">
                    <span className="font-medium text-foreground">{item.name}</span>
                    <div className="text-xs text-muted-foreground">
                        {item.module ? `${item.module}: ` : ''}{item.destination}
                    </div>
                </div>
            ),
        },
        {
            key: 'priority',
            label: t('columns.priority', 'Priority'),
            visible: columnVisibility.priority,
            sortable: true,
            render: (_, item) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                    {item.priority}
                </span>
            ),
        },
        {
            key: 'enabled',
            label: t('columns.enabled', 'Enabled'),
            visible: columnVisibility.enabled,
            sortable: true,
            render: (_, item) => {
                const isToggling = togglingIds.has(item.id);
                return (
                    <button
                        onClick={() => handleToggle(item)}
                        disabled={isToggling}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${item.enabled
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                    >
                        {isToggling ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : item.enabled ? (
                            <Check className="h-3 w-3" />
                        ) : (
                            <X className="h-3 w-3" />
                        )}
                        {item.enabled ? t('common:yes', 'Yes') : t('common:no', 'No')}
                    </button>
                );
            },
        },
        {
            key: 'actions',
            label: t('columns.actions', 'Actions'),
            visible: columnVisibility.actions,
            sortable: false,
        },
    ];

    // Render actions
    const renderActions = useCallback((entry: PriorityRoutingEntry) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common:edit', 'Edit')} position="top">
                <button
                    onClick={() => handleEdit(entry)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>
            <SimpleTooltip content={t('common:duplicate', 'Duplicate')} position="top">
                <button
                    onClick={() => navigate(`/pbx/call-center/priority-routing/new?duplicate=${(entry as any).uuid || entry.id}`)}
                    className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors"
                >
                    <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </button>
            </SimpleTooltip>
            <SimpleTooltip content={t('common:delete', 'Delete')} position="top">
                <button
                    onClick={() => handleDelete(entry)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, handleEdit, handleDelete, navigate]);

    return (
        <ModulePageContainer
            currentView="list"
            listView={
                <div className="pb-20">
                    <DataGridSimple
                        data={data}
                        columns={columns}
                        loading={loading}
                        pagination={{
                            page,
                            limit,
                            total,
                            totalPages,
                        }}
                        searchQuery={search}
                        onSearch={setSearch}
                        onPageChange={setPage}
                        onItemsPerPageChange={(newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        }}
                        onColumnToggle={handleColumnToggle}
                        onSort={handleSort}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        title={t('title', 'Priority Routing')}
                        titleIcon={<ArrowUp className="h-5 w-5 text-primary" />}
                        renderActions={renderActions}
                        actions={
                            <SimpleTooltip content={t('common:add', 'Add')} position="top">
                                <button
                                    onClick={handleCreate}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('common:add', 'Add')}
                                </button>
                            </SimpleTooltip>
                        }
                    />
                </div>
            }
            formView={null}
        >
            {/* Delete Confirmation */}
            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, entry: null })}
                onConfirm={confirmDelete}
                title={t('deleteTitle', 'Delete Priority Routing')}
                message={`${t('deleteConfirm', 'Are you sure you want to delete')} "${deleteModal.entry?.name}"?`}
                confirmText={t('common:delete', 'Delete')}
                cancelText={t('common:cancel', 'Cancel')}
                type="danger"
            />
        </ModulePageContainer>
    );
}
